'use client';

import { useMenuContext } from 'app/[lang]/_contexts/MenuContext';
import { ContextLibraryUploadedFileTypePage, Scalars } from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useContextLibraryDelete } from 'providers/ContextLibraryProviders/useContextLibraryDelete';
import { useContextLibraryNodesCount } from 'providers/ContextLibraryProviders/useContextLibraryNodesCount';
import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { Control } from 'react-hook-form';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { Option } from 'types/Option';
import useSearchParamsContextLibrary, {
  FiltersContextLibraryForm
} from './useSearchParamsContextLibrary';

type TContextLibraryContext = {
  deferredSearchQuery?: string;
  searchQuery?: string;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  data?: ContextLibraryUploadedFileTypePage;
  contextId: string;
  refetch: () => Promise<void>;
  isLoading: boolean;
  hasNodes: Scalars['Boolean'];
  isChecking: boolean;
  isSelectDisabled: boolean;
  handleOpenEditContextLibraryModal: (contextId: string) => void;
  handleOpenDeleteContextLibraryModal: (contextId: string) => void;
  handleCloseDeleteContextLibraryModal: () => void;
  handleCloseEditContextLibraryModal: () => void;
  handleDeleteContextLibrary: () => void;
  modalDeleteContextLibraryOpen: boolean;
  modalEditContextLibraryOpen: boolean;
  optionsSortAttribute: Option[];
  optionsSortOrder: Option[];
  optionsFilterTags: Option[];
  control: Control<FiltersContextLibraryForm>;
  defaultTags: string[];
  handleClear: () => void;
};

const initialState = {
  deferredSearchQuery: '',
  searchQuery: '',
  contextId: '',
  page: 1,
  setPage: () => {},
  data: {} as ContextLibraryUploadedFileTypePage,
  refetch: async () => {},
  isLoading: true,
  hasNodes: false,
  isChecking: true,
  isSelectDisabled: false,
  handleOpenDeleteContextLibraryModal: () => {},
  handleOpenEditContextLibraryModal: () => {},
  handleCloseDeleteContextLibraryModal: () => {},
  handleCloseEditContextLibraryModal: () => {},
  handleDeleteContextLibrary: () => {},
  handleClear: () => {},
  modalDeleteContextLibraryOpen: true,
  modalEditContextLibraryOpen: true,
  optionsSortAttribute: [],
  optionsSortOrder: [],
  optionsFilterTags: [],
  control: {} as Control<FiltersContextLibraryForm>,
  defaultTags: []
};

const ContextLibraryContext = createContext<TContextLibraryContext>(initialState);

const useContextLibraryContext = (): TContextLibraryContext => useContext(ContextLibraryContext);

const ContextLibraryProvider: FC<PropsWithChildren<unknown>> = ({ children }): JSX.Element => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const { handleScrollTop } = useMenuContext();

  const { mutateAsync: contextLibraryDestroy } = useContextLibraryDelete({
    onSuccess: () => {
      handleScrollTop();
      toaster.success(
        t('messages.success.file_deleted.paragraph'),
        t('messages.success.file_deleted.title')
      );
    },
    onError: (error: unknown) => {
      handleScrollTop();
      notifyException(error, ESpansException.ContextLibraryException);
      toaster.error(
        t('messages.error.file_deleted.paragraph'),
        t('messages.error.file_deleted.title')
      );
    }
  });

  const [modalDeleteContextLibraryOpen, setModalDeleteContextLibraryOpen] = useState(false);
  const [modalEditContextLibraryOpen, setModalEditContextLibraryOpen] = useState(false);
  const [contextId, setContextId] = useState('');

  const { data: nodesCount, refetch: refetchNodesCount } = useContextLibraryNodesCount();

  const {
    control,
    data,
    defaultTags,
    refetch,
    isLoading,
    isPending,
    deferredSearchQuery,
    searchQuery,
    page,
    setPage,
    optionsSortAttribute,
    optionsSortOrder,
    optionsFilterTags,
    handleClear
  } = useSearchParamsContextLibrary();

  const handleRefetch = useCallback(async () => {
    await refetch();
    await refetchNodesCount();
  }, [refetch, refetchNodesCount]);

  const handleOpenDeleteContextLibraryModal = (contextId: string) => {
    setModalDeleteContextLibraryOpen(true);
    setContextId(contextId);
  };

  const handleOpenEditContextLibraryModal = (contextId: string) => {
    setModalEditContextLibraryOpen(true);
    setContextId(contextId);
  };

  const handleCloseDeleteContextLibraryModal = () => {
    setContextId('');
    setModalDeleteContextLibraryOpen(false);
  };

  const handleCloseEditContextLibraryModal = () => {
    setContextId('');
    setModalEditContextLibraryOpen(false);
  };

  const handleDeleteContextLibrary = useCallback(async () => {
    await contextLibraryDestroy({
      contextId
    });
    await handleRefetch();
    setModalDeleteContextLibraryOpen(false);
  }, [contextId, contextLibraryDestroy]);

  const hasNodes = !!nodesCount;
  const isChecking = isLoading || isPending;
  const isSelectDisabled = !hasNodes;

  const onFetch = useCallback(async () => {
    if (!isChecking) {
      if (!!hasNodes && !pathname.includes('/smart-tasks/context-library/list'))
        return router.push('/smart-tasks/context-library/list');
      if (!hasNodes) return router.push('/smart-tasks/context-library');
    }
  }, [hasNodes, isChecking, pathname, router]);

  useEffect(() => {
    onFetch();
  }, [onFetch]);

  return (
    <ContextLibraryContext.Provider
      value={{
        deferredSearchQuery,
        searchQuery,
        defaultTags,
        page,
        setPage,
        contextId,
        data,
        refetch: handleRefetch,
        isLoading,
        hasNodes,
        isChecking,
        isSelectDisabled,
        handleOpenDeleteContextLibraryModal,
        handleOpenEditContextLibraryModal,
        handleCloseDeleteContextLibraryModal,
        handleCloseEditContextLibraryModal,
        handleDeleteContextLibrary,
        handleClear,
        modalDeleteContextLibraryOpen,
        modalEditContextLibraryOpen,
        optionsSortAttribute,
        optionsSortOrder,
        optionsFilterTags,
        control
      }}
    >
      {children}
    </ContextLibraryContext.Provider>
  );
};

export { ContextLibraryProvider, useContextLibraryContext };
